/**
 * @file repOrderRequestApi.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description The calls behind rep order requests (routes/repOrderRequests.js)
 *              and the hand-off that carries a request to the screen that makes
 *              its order. Client only.
 *
 *   The hand-off: the office opens a request on Sales Orders' "Rep requests",
 *   and Create / Edit Customer Order reads it on arrival (sessionStorage
 *   `repOrderRequest`, as the other editors' hand-offs — utils/editHandoff),
 *   honoured for ten minutes and read once.
 */

import { API_URL } from '../config';

export const HANDOFF_KEY = 'repOrderRequest';
const HANDOFF_MAX_MS = 10 * 60 * 1000;

/** A refusal from the server as an Error carrying its code (and status). */
async function call(method, path, body) {
  const res = await fetch(`${API_URL}/api/rep-order-requests${path}`, {
    method,
    credentials: 'include',
    ...(body !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {})
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const err = new Error(json.error || json.detail || `The server said no (${res.status})`);
    err.code = json.code || null;
    err.status = res.status;
    err.errors = json.errors || null;
    throw err;
  }
  return json;
}

export const repRequestsApi = {
  status: () => call('GET', '/status'),
  form: () => call('GET', '/form'),
  items: (accountId) => call('GET', `/items/${accountId}`),
  prefillOrder: (orderId) => call('GET', `/prefill/order/${orderId}`),
  prefillStandingOrder: (id) => call('GET', `/prefill/standing-order/${id}`),
  mine: () => call('GET', '/mine'),
  send: (body) => call('POST', '', body),
  withdraw: (id) => call('POST', `/${id}/withdraw`),
  list: (tab) => call('GET', `?tab=${tab === 'complete' ? 'complete' : 'open'}`),
  count: () => call('GET', '/count'),
  get: (id) => call('GET', `/${id}`),
  open: (id) => call('POST', `/${id}/open`),
  close: (id, reason) => call('POST', `/${id}/close`, { reason })
};

/** Hand a request to the order screen (read once, on arrival). */
export function handOffRequest(request) {
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ request, at: Date.now() }));
    return true;
  } catch (e) {
    return false;
  }
}

/** The request handed over, if one is waiting and fresh; it is removed either way. */
export function takeHandOff() {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    sessionStorage.removeItem(HANDOFF_KEY);
    if (!raw) return null;
    const { request, at } = JSON.parse(raw);
    if (!request || !at || Date.now() - at > HANDOFF_MAX_MS) return null;
    return request;
  } catch (e) {
    return null;
  }
}

/** Is a hand-off waiting (without taking it)? */
export function handOffWaiting() {
  try {
    return !!sessionStorage.getItem(HANDOFF_KEY);
  } catch (e) {
    return false;
  }
}
